import { useMemo } from 'react';

import {
  SerializedChatPromptTemplateMessagesKwarg,
  convertManifestToMessages,
} from '@/Pages/Playground/components/manifest/serialized/prompts/ManifestChatPromptTemplate.utils';
import { StructuredPromptSchema } from '@/Pages/Playground/components/manifest/serialized/prompts/ManifestStructuredPrompt';
import { getPromptManifest } from '@/Pages/Playground/utils/Playground.utils';
import { CommitWithLookupsSchema } from '@/types/schema';

import { isChatPrompt } from '../../utils';
import { GetMessageDiffsArgs, StoredMessageDiffsRecord } from '../types';
import {
  CommitChatPromptDiff,
  CommitChatPromptManifest,
} from './CommitChatPromptManifest';
import { CommitPromptDiff, CommitPromptManifest } from './CommitPromptManifest';
import { WholePromptDiff } from './WholePromptDiff';

export const CommitManifestView = ({
  showDiff,
  currentCommit,
  previousCommit,
  getMessageDiffs,
}: {
  showDiff: boolean;
  currentCommit: CommitWithLookupsSchema;
  previousCommit?: CommitWithLookupsSchema;
  getMessageDiffs: (args: GetMessageDiffsArgs) => StoredMessageDiffsRecord;
}) => {
  const isChat = useMemo(
    () => isChatPrompt(currentCommit?.manifest),
    [currentCommit?.manifest]
  );

  const currentCommitPromptManifest = useMemo(
    () => getPromptManifest(currentCommit?.manifest),
    [currentCommit?.manifest]
  );

  const previousCommitPromptManifest = useMemo(
    () =>
      previousCommit ? getPromptManifest(previousCommit?.manifest) : undefined,
    [previousCommit]
  );

  const diffContent = useMemo(() => {
    if (!previousCommitPromptManifest) return null;

    const isPreviousChat =
      previousCommitPromptManifest &&
      isChatPrompt(previousCommitPromptManifest);

    if (isChat !== isPreviousChat) {
      return (
        <WholePromptDiff
          commitId={currentCommit.id}
          getMessageDiffs={getMessageDiffs}
          previousCommit={previousCommitPromptManifest}
          currentCommit={currentCommitPromptManifest}
          isPreviousChat={isPreviousChat}
        />
      );
    }

    if (isChat) {
      const currentCommitMsgs = currentCommitPromptManifest?.kwargs.messages
        ? convertManifestToMessages(currentCommitPromptManifest.kwargs.messages)
        : [];

      const previousCommitMsgs = previousCommitPromptManifest?.kwargs.messages
        ? convertManifestToMessages(
            previousCommitPromptManifest.kwargs.messages
          )
        : [];

      const currentCommitSchema = currentCommitPromptManifest?.kwargs.schema_;
      const previousCommitSchema = previousCommitPromptManifest?.kwargs.schema_;

      return (
        <CommitChatPromptDiff
          commitId={currentCommit.id}
          getMessageDiffs={getMessageDiffs}
          currentCommitMsgs={currentCommitMsgs}
          previousCommitMsgs={previousCommitMsgs}
          currentCommitSchema={currentCommitSchema}
          previousCommitSchema={previousCommitSchema}
        />
      );
    }

    const currentCommitMsg = currentCommitPromptManifest?.kwargs.template;
    const previousCommitMsg = previousCommitPromptManifest?.kwargs.template;

    return (
      <CommitPromptDiff
        currentCommitMsg={currentCommitMsg}
        previousCommitMsg={previousCommitMsg}
      />
    );
  }, [
    currentCommitPromptManifest,
    previousCommitPromptManifest,
    isChat,
    getMessageDiffs,
    currentCommit.id,
  ]);

  const manifestContent = useMemo(() => {
    const memoizedKwargs = currentCommitPromptManifest?.kwargs;

    return isChat ? (
      <CommitChatPromptManifest
        key={currentCommit.id}
        kwargs={
          memoizedKwargs as Record<string, unknown> & {
            messages: SerializedChatPromptTemplateMessagesKwarg;
            schema_?: StructuredPromptSchema;
          }
        }
      />
    ) : (
      <CommitPromptManifest
        key={currentCommit.id}
        kwargs={
          memoizedKwargs as Record<string, unknown> & {
            template_format: string;
            template: string;
            input_variables: string[];
          }
        }
      />
    );
  }, [currentCommitPromptManifest, isChat, currentCommit.id]);

  return showDiff && diffContent ? diffContent : manifestContent;
};
